import React, { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { Random } from '@zedoc/random';
import RecipientFilterOption from '../../../../common/models/RecipientFilterOption';
import {
  apiZedocProjectVariablesSummary,
  apiZedocSearchRecipientsFilterOptions,
} from '../../../../common/api/zedoc';
import {
  FILTER_CONDITION__FULL_TEXT,
  FILTER_TYPE__ALL_OF,
} from '../../../../common/constants';
import createSelectors from '../../../../common/selectors/createSelectors';
import { default as VariableSelect } from '../../../../common/selectors/Variable';
import defaultFilters from './defaultFilters';
import { FILTER_CATEGORY__VARIABLE } from '../../../../components/Filters';
import usePatientFilters from '../../../../utils/usePatientFilters';
import FiltersToolbar from './FiltersToolbar';

const RecipientFilterOptionSelect = createSelectors(RecipientFilterOption, {});

const ConnectedFilters = () => {
  const variablesDb = useSelector(
    VariableSelect.all()
      // eslint-disable-next-line no-underscore-dangle
      .satisfying((variable) => !isEmpty(variable._projects))
      .byId(),
  );
  useDDPSubscription(apiZedocProjectVariablesSummary.withParams({}));
  const presets = usePatientFilters({
    defaultFilters: useMemo(() => {
      return defaultFilters.filter((preset) => {
        if (!preset?.meta?.includeIfVariableIdPresent) {
          return true;
        }
        return !!variablesDb[preset.meta.includeIfVariableIdPresent];
      });
    }, [variablesDb]),
    defaultCategory: FILTER_CATEGORY__VARIABLE,
  });
  const getOptionsSubscription = useCallback(
    ({
      searchText,
      type,
      condition,
      state,
      settings,
      pageIndex,
      resultsPerPage,
    }) => {
      return apiZedocSearchRecipientsFilterOptions.withParams({
        controlId: '$meta.id',
        searchText,
        filter: {
          type,
          condition,
          state,
          settings,
        },
        pageIndex,
        resultsPerPage,
        sorterFilter: {
          label: 1,
        },
      });
    },
    [],
  );

  const simpleFilters = useMemo(
    () => [
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // Full Name
            {
              ...defaultFilters[0],
              id: Random.id(),
              condition: FILTER_CONDITION__FULL_TEXT,
              state: { text: '' },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // First Name
            {
              ...defaultFilters[1],
              id: Random.id(),
              condition: FILTER_CONDITION__FULL_TEXT,
              state: { text: '' },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
      {
        id: Random.id(),
        type: FILTER_TYPE__ALL_OF,
        settings: {
          filters: [
            // Last Name
            {
              ...defaultFilters[2],
              id: Random.id(),
              condition: FILTER_CONDITION__FULL_TEXT,
              state: { text: '' },
            },
          ],
        },
        meta: {
          nested: true,
        },
      },
    ],
    [],
  );

  return (
    <FiltersToolbar
      presets={presets}
      simpleFilters={simpleFilters}
      optionsSelector={RecipientFilterOptionSelect}
      optionsSubscription={getOptionsSubscription}
    />
  );
};

ConnectedFilters.propTypes = {};

export default ConnectedFilters;
